import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    useVideoSession,
    onVideoSessionClosed,
    onVideoParticipantsChange,
    leaveVideoSession,
    onUnmuteRequest,
    onForcedMute,
} from '../../../hooks/useVideoSession';
import { leaveMeeting as leaveMeetingAction, setSidebarTab } from '../../../redux/reducers/zoom';
import { endClassSession, markSessionRecording } from '../../../redux/actions/zoom';
import {
    connectSocket,
    emitZoomParticipantJoin,
    emitZoomParticipantLeave,
} from '../../../services/socket';
import { useSubsessions } from '../../../hooks/useSubsessions';
import { useWhiteboard } from '../../../hooks/useWhiteboard';
import { useLiveClassState } from '../../../hooks/liveClassStore';
import { setOwnHandRaised, setScreenFrozen } from '../../../hooks/useLiveClassSignals';
import { VideoMainStage } from './VideoMainStage';
import { VideoParticipantRail } from './VideoParticipantRail';
import { VideoCallControls } from './VideoCallControls';
import { LiveFloatingControls } from './LiveFloatingControls';
import { VideoShareStage } from './VideoShareStage';
import { VideoWhiteboardStage } from './VideoWhiteboardStage';
import { SubsessionsPanel } from './SubsessionsPanel';
import './video-session.css';

export const VideoSessionEmbed = ({
    credentials,
    courseId,
    onLeave,
    /** En móvil las tabs viven en una hoja: hay que abrirla al elegir chat o participantes. */
    onOpenSidePanel = null,
}) => {
    const dispatch = useDispatch();
    const shellRef = useRef(null);
    const stageRef = useRef(null);
    const {
        joinSession,
        leaveSession,
        toggleAudio,
        toggleVideo,
        toggleShare,
        stopShare,
        mediaState,
        devices,
        refreshDevices,
        selectMicrophone,
        selectSpeaker,
        selectCamera,
        startCloudRecording,
        stopCloudRecording,
        pauseCloudRecording,
        resumeCloudRecording,
        muteAllStudents,
        recordingStatus,
        recordingBusy,
    } = useVideoSession();
    const subsessions = useSubsessions();
    const whiteboard = useWhiteboard();

    const screenFrozen = useLiveClassState((s) => s.screenFrozen);
    const recordingState = useLiveClassState((s) => s.recordingState);
    const selfUserId = useLiveClassState((s) => s.selfUserId);
    const raisedHandUserIds = useLiveClassState((s) => s.raisedHandUserIds);

    const joinAttemptRef = useRef(0);
    const onLeaveRef = useRef(onLeave);
    const announcedJoinRef = useRef(false);
    const endingForAllRef = useRef(false);

    const [connecting, setConnecting] = useState(true);
    const [participants, setParticipants] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [freezeBusy, setFreezeBusy] = useState(false);
    const [shareState, setShareState] = useState({ active: false, selfSharing: false });
    const [subsessionsOpen, setSubsessionsOpen] = useState(false);
    const [askingHelp, setAskingHelp] = useState(false);
    const [whiteboardNoticeHidden, setWhiteboardNoticeHidden] = useState(false);

    onLeaveRef.current = onLeave;

    const sessionId = credentials?.session?._id;
    const zoomCallId = credentials?.session?.zoomCallId || null;
    const isHost = Boolean(credentials?.isHost);
    const sessionTitle = credentials?.session?.topic
        || (credentials?.isHost ? 'Clase en vivo' : 'Videollamada');
    const sessionKey = credentials
        ? `${credentials.sessionName}:${credentials.signature}`
        : null;

    const announceLeave = useCallback((reason = 'ended') => {
        if (!announcedJoinRef.current) return;
        announcedJoinRef.current = false;
        if (courseId && sessionId) {
            emitZoomParticipantLeave(courseId, sessionId, reason);
        }
    }, [courseId, sessionId]);

    useEffect(() => onVideoParticipantsChange(setParticipants), []);

    useEffect(() => onUnmuteRequest(() => {
        toast('El profesor te pide activar tu micrófono', { icon: '🎤' });
    }), []);

    useEffect(() => onForcedMute(() => {
        toast('El profesor silenció tu micrófono', { icon: '🔇' });
    }), []);

    useEffect(() => {
        const onFsChange = () => {
            const el = shellRef.current;
            setIsFullscreen(Boolean(document.fullscreenElement && document.fullscreenElement === el));
        };
        document.addEventListener('fullscreenchange', onFsChange);
        return () => document.removeEventListener('fullscreenchange', onFsChange);
    }, []);

    useEffect(() => {
        const unsubscribe = onVideoSessionClosed(async () => {
            announceLeave();
            if (endingForAllRef.current) return;
            dispatch(leaveMeetingAction());
            onLeaveRef.current?.({ closedByRemote: true });
        });
        return unsubscribe;
    }, [dispatch, announceLeave]);

    useEffect(() => {
        if (!sessionKey) return undefined;

        const attemptId = joinAttemptRef.current + 1;
        joinAttemptRef.current = attemptId;
        setConnecting(true);
        announcedJoinRef.current = false;

        let cancelled = false;

        const start = async () => {
            try {
                await new Promise((resolve) => {
                    requestAnimationFrame(() => requestAnimationFrame(resolve));
                });
                if (cancelled || joinAttemptRef.current !== attemptId) return;

                await joinSession(credentials);
                if (cancelled || joinAttemptRef.current !== attemptId) return;

                setConnecting(false);

                try {
                    await connectSocket();
                    if (courseId && sessionId) {
                        emitZoomParticipantJoin(courseId, sessionId);
                        announcedJoinRef.current = true;
                    }
                } catch (err) {
                    console.warn('[VideoSDK] No se pudo anunciar ingreso:', err?.message);
                }

                toast.success(
                    credentials.isHost
                        ? 'Conectado a la videollamada (anfitrión)'
                        : 'Conectado a la clase',
                );
            } catch (err) {
                if (cancelled || joinAttemptRef.current !== attemptId) {
                    console.warn('[VideoSDK embed] join falló pero el intento ya fue cancelado/superseded', {
                        attemptId,
                        cancelled,
                        currentAttempt: joinAttemptRef.current,
                        reason: err?.reason || err?.message,
                    });
                    return;
                }
                setConnecting(false);
                const msg = err?.reason || err?.message || 'No se pudo unir a la videollamada';
                if (process.env.NODE_ENV !== 'production') {
                    console.error('[VideoSDK embed] No se pudo unir:', {
                        attemptId,
                        msg,
                        errorCode: err?.errorCode,
                        sessionName: credentials?.sessionName,
                    });
                }
                toast.error(String(msg));
                dispatch(leaveMeetingAction());
                onLeaveRef.current?.();
            }
        };

        start();

        return () => {
            cancelled = true;
            joinAttemptRef.current += 1;
            announceLeave();
            void leaveSession();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionKey, joinSession, leaveSession, dispatch, courseId, sessionId]);

    const handleLeave = useCallback(async () => {
        if (document.fullscreenElement) {
            try { await document.exitFullscreen(); } catch { /* ignore */ }
        }
        announceLeave('self');
        await leaveVideoSession({ notify: false });
        dispatch(leaveMeetingAction());
        onLeaveRef.current?.({ selfLeft: true });
    }, [announceLeave, dispatch]);

    const handleStartRecording = useCallback(async () => {
        if (recordingBusy || !sessionId) return;
        try {
            await startCloudRecording();
            await dispatch(markSessionRecording(sessionId, true, zoomCallId));
            toast.success('Grabando sesión. Se vinculará a esta clase.');
        } catch (err) {
            toast.error(err?.message || err?.response?.data?.message || 'No se pudo iniciar la grabación');
        }
    }, [recordingBusy, sessionId, zoomCallId, startCloudRecording, dispatch]);

    const handleStopRecording = useCallback(async () => {
        if (recordingBusy || !sessionId) return;
        try {
            await stopCloudRecording();
            await dispatch(markSessionRecording(sessionId, false, zoomCallId));
            toast.success('Grabación detenida. Zoom la procesará y aparecerá en esta clase.');
        } catch (err) {
            toast.error(err?.message || err?.response?.data?.message || 'No se pudo detener la grabación');
        }
    }, [recordingBusy, sessionId, zoomCallId, stopCloudRecording, dispatch]);

    /**
     * Pausar no cierra la grabación: la sesión sigue marcada como grabando para
     * que al reanudar se siga usando el mismo archivo de Zoom.
     */
    const handlePauseRecording = useCallback(async () => {
        if (recordingBusy) return;
        try {
            await pauseCloudRecording();
            toast.success('Grabación pausada. La clase continúa normal.');
        } catch (err) {
            toast.error(err?.message || 'No se pudo pausar la grabación');
        }
    }, [recordingBusy, pauseCloudRecording]);

    const handleResumeRecording = useCallback(async () => {
        if (recordingBusy) return;
        try {
            await resumeCloudRecording();
            toast.success('Grabación reanudada');
        } catch (err) {
            toast.error(err?.message || 'No se pudo reanudar la grabación');
        }
    }, [recordingBusy, resumeCloudRecording]);

    /** La barra superior mantiene un único botón: inicia o detiene. */
    const handleToggleRecording = useCallback(async () => {
        if (!isHost) return;
        if (recordingState === 'idle') await handleStartRecording();
        else await handleStopRecording();
    }, [isHost, recordingState, handleStartRecording, handleStopRecording]);

    const handleEndForAll = useCallback(async () => {
        if (!sessionId || endingForAllRef.current) return;
        endingForAllRef.current = true;
        try {
            const isRecording = recordingStatus === 'Recording' || recordingStatus === 'Paused';
            if (isRecording) {
                try {
                    await stopCloudRecording();
                    await dispatch(markSessionRecording(sessionId, false, zoomCallId));
                } catch (recErr) {
                    console.warn('[VideoSDK] No se pudo detener grabación al terminar:', recErr?.message);
                }
            }
            announceLeave();
            await dispatch(endClassSession(sessionId));
            toast.success('Videollamada terminada para todos');
            onLeaveRef.current?.({ alreadyEndedByHost: true });
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo terminar la videollamada');
            await handleLeave();
        } finally {
            endingForAllRef.current = false;
        }
    }, [announceLeave, dispatch, sessionId, zoomCallId, handleLeave, recordingStatus, stopCloudRecording]);

    const handleToggleFullscreen = useCallback(async () => {
        const el = shellRef.current;
        if (!el) {
            toast.error('No se encontró el contenedor de la videollamada');
            return;
        }
        try {
            if (document.fullscreenElement === el) {
                await document.exitFullscreen();
                return;
            }
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
            if (typeof el.requestFullscreen === 'function') {
                await el.requestFullscreen();
            } else if (typeof el.webkitRequestFullscreen === 'function') {
                await el.webkitRequestFullscreen();
            } else {
                toast.error('Tu navegador no soporta pantalla completa');
            }
        } catch (err) {
            console.warn('[VideoSDK] fullscreen:', err);
            toast.error(err?.message || 'No se pudo activar pantalla completa');
        }
    }, []);

    const safeToggle = (fn, label) => async (...args) => {
        try {
            await fn(...args);
        } catch (err) {
            toast.error(err?.message || `No se pudo ${label}`);
        }
    };

    const selfSharing = Boolean(shareState.selfSharing || mediaState.sharing);
    const anyoneSharing = Boolean(shareState.active || selfSharing);
    // El alumno sigue viendo el share (Zoom conserva el último fotograma); solo se le avisa.
    const frozenShareForMe = screenFrozen && !isHost && anyoneSharing;
    // El contenido ocupa el escenario: el video del profesor pasa a la columna de participantes.
    const stageBusy = anyoneSharing || whiteboard.isOpen;
    const showRail = stageBusy && !connecting;

    const handleOpenLiveTab = useCallback(() => {
        dispatch(setSidebarTab('live'));
        onOpenSidePanel?.();
    }, [dispatch, onOpenSidePanel]);

    const handleStopShare = useCallback(async () => {
        try {
            await stopShare();
        } catch (err) {
            toast.error(err?.message || 'No se pudo dejar de compartir');
        }
    }, [stopShare]);

    const whiteboardPresenting = whiteboard.attachMode === 'present' || whiteboard.wantsPresent;

    const handleToggleShare = useCallback(async () => {
        if (selfSharing) {
            await handleStopShare();
            return;
        }
        if (whiteboard.isOpen) {
            toast.error('Cierra la pizarra antes de compartir pantalla');
            return;
        }
        try {
            await toggleShare();
        } catch (err) {
            toast.error(err?.message || 'No se pudo compartir pantalla');
        }
    }, [selfSharing, handleStopShare, whiteboard.isOpen, toggleShare]);

    const handleToggleWhiteboard = useCallback(async () => {
        if (!isHost) return;
        try {
            if (whiteboardPresenting) {
                await whiteboard.closeWhiteboard();
                return;
            }
            if (selfSharing) {
                toast.error('Deja de compartir pantalla antes de abrir la pizarra');
                return;
            }
            whiteboard.openWhiteboard();
        } catch (err) {
            toast.error(err?.message || 'No se pudo abrir la pizarra');
        }
    }, [isHost, whiteboard, whiteboardPresenting, selfSharing]);

    const whiteboardVisible = isHost;
    const whiteboardDisabled = !whiteboardPresenting && (!whiteboard.ready || !whiteboard.supported || !whiteboard.canStart);

    const whiteboardTitle = (() => {
        if (whiteboardPresenting) return 'Cerrar pizarra';
        if (whiteboard.ready && !whiteboard.supported) return 'La pizarra no está disponible en este dispositivo';
        if (whiteboardDisabled) return 'No puedes abrir la pizarra en este momento';
        return 'Abrir pizarra';
    })();

    const canAskForHelp = !isHost && subsessions.inSubsession;

    const handleAskForHelp = useCallback(async () => {
        if (askingHelp) return;
        setAskingHelp(true);
        try {
            await subsessions.askForHelp();
            toast.success('Le avisamos al profesor que necesitas ayuda');
        } catch (err) {
            toast.error(err?.message || 'No se pudo pedir ayuda');
        } finally {
            setAskingHelp(false);
        }
    }, [askingHelp, subsessions]);

    const handRaised = selfUserId != null && raisedHandUserIds.includes(selfUserId);

    const handleToggleHand = useCallback(async () => {
        try {
            await setOwnHandRaised(!handRaised);
        } catch (err) {
            toast.error(err?.message || 'No se pudo cambiar el estado de tu mano');
        }
    }, [handRaised]);

    const handleMuteAll = useCallback(async () => {
        try {
            const muted = await muteAllStudents();
            if (!muted) toast('Todos los estudiantes ya estaban silenciados');
            else toast.success(`Silenciaste a ${muted} estudiante${muted === 1 ? '' : 's'}`);
        } catch (err) {
            toast.error(err?.message || 'No se pudo silenciar a los estudiantes');
        }
    }, [muteAllStudents]);

    /** Pausa/reanuda el share propio: los alumnos se quedan con el último fotograma. */
    const handleToggleFreeze = useCallback(async () => {
        if (freezeBusy) return;
        setFreezeBusy(true);
        try {
            await setScreenFrozen(!screenFrozen);
        } catch (err) {
            toast.error(err?.message || 'No se pudo cambiar el estado de la pantalla');
        } finally {
            setFreezeBusy(false);
        }
    }, [screenFrozen, freezeBusy]);

    const handleLeaveSubsession = useCallback(async () => {
        try {
            await subsessions.leaveSubsession();
        } catch (err) {
            toast.error(err?.message || 'No se pudo volver a la sala principal');
        }
    }, [subsessions]);

    return (
        <div
            ref={shellRef}
            className={`video-session-shell ${isFullscreen ? 'is-fullscreen' : ''}`}
        >
            <VideoCallControls
                isHost={isHost}
                mediaState={{ ...mediaState, sharing: selfSharing }}
                participantCount={participants.length}
                title={sessionTitle}
                isFullscreen={isFullscreen}
                subsessionsPanelOpen={subsessionsOpen}
                subsessionAlerts={subsessions.helpRequests.length}
                canAskForHelp={canAskForHelp}
                whiteboardVisible={whiteboardVisible}
                whiteboardActive={whiteboard.isOpen}
                whiteboardPresenting={whiteboardPresenting}
                whiteboardDisabled={whiteboardDisabled}
                whiteboardTitle={whiteboardTitle}
                devices={devices}
                onRefreshDevices={refreshDevices}
                onSelectMic={safeToggle(selectMicrophone, 'cambiar el micrófono')}
                onSelectSpeaker={safeToggle(selectSpeaker, 'cambiar el altavoz')}
                onSelectCamera={safeToggle(selectCamera, 'cambiar la cámara')}
                onToggleSubsessions={() => setSubsessionsOpen((v) => !v)}
                onAskForHelp={handleAskForHelp}
                onToggleAudio={safeToggle(toggleAudio, 'cambiar el micrófono')}
                onToggleVideo={safeToggle(toggleVideo, 'cambiar la cámara')}
                onToggleShare={handleToggleShare}
                onToggleWhiteboard={handleToggleWhiteboard}
                onToggleFullscreen={handleToggleFullscreen}
                onLeave={handleLeave}
                onEndForAll={handleEndForAll}
                onOpenLiveUsers={handleOpenLiveTab}
                onOpenChat={() => {
                    dispatch(setSidebarTab('chat'));
                    onOpenSidePanel?.();
                }}
                handRaised={handRaised}
                onToggleHand={handleToggleHand}
                onMuteAll={handleMuteAll}
                recordingStatus={recordingStatus}
                recordingBusy={recordingBusy}
                onToggleRecording={handleToggleRecording}
            />

            {(subsessions.currentSubsessionName || subsessions.closingCountdown != null) && (
                <div className="vc-bo-banner">
                    <span className="vc-bo-banner__tag">Subgrupo</span>
                    <span>
                        {subsessions.currentSubsessionName
                            ? `${isHost ? 'Supervisando' : 'Estás en'} ${subsessions.currentSubsessionName}`
                            : 'Los grupos se están cerrando'}
                    </span>
                    {subsessions.closingCountdown != null && (
                        <span>· vuelven a la sala principal en {subsessions.closingCountdown}s</span>
                    )}
                    <span className="vc-bo-banner__spacer" />
                    {canAskForHelp && (
                        <button
                            type="button"
                            className="vc-bo-banner__btn"
                            disabled={askingHelp}
                            onClick={handleAskForHelp}
                        >
                            Pedir ayuda
                        </button>
                    )}
                    {isHost && subsessions.currentSubsessionId && (
                        <button
                            type="button"
                            className="vc-bo-banner__btn"
                            onClick={handleLeaveSubsession}
                        >
                            Volver a sala principal
                        </button>
                    )}
                </div>
            )}

            {whiteboard.ready && !whiteboard.supported && !whiteboardNoticeHidden && (
                <div className="vc-wb-banner">
                    <span className="vc-wb-banner__tag">Pizarra</span>
                    <span>
                        Tu dispositivo o navegador no soporta la pizarra colaborativa.
                        Si alguien la abre, solo verás la imagen compartida.
                    </span>
                    <span className="vc-wb-banner__spacer" />
                    <button
                        type="button"
                        className="vc-wb-banner__btn"
                        onClick={() => setWhiteboardNoticeHidden(true)}
                    >
                        Entendido
                    </button>
                </div>
            )}

            {isHost && screenFrozen && (
                <div className="vc-freeze-banner" role="status">
                    <span className="vc-freeze-banner__tag">Congelada</span>
                    <span>
                        Los alumnos ven la última imagen de tu pantalla. Puedes usar tu
                        computadora sin que vean lo que haces; siguen escuchándote.
                    </span>
                    <span className="vc-freeze-banner__spacer" />
                    <button
                        type="button"
                        className="vc-freeze-banner__btn"
                        disabled={freezeBusy}
                        onClick={handleToggleFreeze}
                    >
                        Descongelar pantalla
                    </button>
                </div>
            )}

            <div
                ref={stageRef}
                className={[
                    'vc-stage',
                    anyoneSharing ? 'is-sharing' : '',
                    whiteboard.isOpen ? 'is-whiteboard' : '',
                    selfSharing ? 'has-share-banner' : '',
                    showRail ? 'has-rail' : '',
                ].filter(Boolean).join(' ')}
            >
                {selfSharing && (
                    <div className="vc-share-banner">
                        <span className="vc-share-banner__dot" />
                        <span>Estás compartiendo tu pantalla</span>
                        <button
                            type="button"
                            className="vc-share-banner__stop"
                            onClick={handleStopShare}
                        >
                            Dejar de compartir
                        </button>
                    </div>
                )}
                <VideoMainStage hidden={stageBusy} />
                <VideoShareStage
                    onStopShare={selfSharing ? handleStopShare : null}
                    onShareStateChange={setShareState}
                />
                <VideoWhiteboardStage whiteboard={whiteboard} isHost={isHost} />
                {showRail ? <VideoParticipantRail /> : null}
                {frozenShareForMe && (
                    <div className="vc-frozen-chip" role="status">
                        <span className="vc-frozen-chip__dot" aria-hidden="true" />
                        El profesor pausó su pantalla
                    </div>
                )}
                {connecting && (
                    <div className="vc-connecting">Conectando a la videollamada…</div>
                )}
                {!connecting && (
                    <LiveFloatingControls
                        onToggleAudio={safeToggle(toggleAudio, 'cambiar el micrófono')}
                        onToggleVideo={safeToggle(toggleVideo, 'cambiar la cámara')}
                        onToggleShare={handleToggleShare}
                        onToggleFreeze={handleToggleFreeze}
                        freezeBusy={freezeBusy}
                        onStartRecording={handleStartRecording}
                        onStopRecording={handleStopRecording}
                        onPauseRecording={handlePauseRecording}
                        onResumeRecording={handleResumeRecording}
                        onOpenLiveTab={handleOpenLiveTab}
                        onMuteAll={handleMuteAll}
                        onToggleHand={handleToggleHand}
                    />
                )}
            </div>

            {isHost && subsessionsOpen && (
                <SubsessionsPanel subsessions={subsessions} onClose={() => setSubsessionsOpen(false)} />
            )}
        </div>
    );
};
